import React, { useState, useEffect, useRef } from 'react';
import { Button, Space, Modal, Form, message, Select, Empty, Input } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { getAllDevices, addDevice, updateDevice, deleteDevice, batchDeleteDevices, getAllRooms, downloadDeviceTemplate, batchImportDevices } from '../../api';
import ListToolbar from '../../components/ListToolbar';
import { exportToCSV } from '../../utils/exportUtils';
import PageTitle from '../../components/PageTitle';
import ResizableTable from '../../components/ResizableTable';
import { showBatchImportResult, showBatchImportHttpError } from '../../utils/batchImportModal';

const { Option } = Select;
const DEVICE_STATUS_OPTIONS = [
  { value: '1', label: '在线/正常' },
  { value: '0', label: '离线/故障' },
];
/** 编辑用：仅「公司」「其他」；清空表示未设置（未审核） */
const LOCK_SOURCE_EDIT_OPTIONS = [
  { value: '公司', label: '公司' },
  { value: '其他', label: '其他' },
];

const lockReviewLabel = (record) => {
  const src = String(record?.lock_source ?? '').trim();
  if (src === '公司' || src === '其他') return '已审核';
  return '未审核';
};

const DEVICE_TYPE_OPTIONS = [
  { value: 1, label: '电表' },
  { value: 2, label: '水表' },
  { value: 3, label: '门锁' },
];
// ✅ 固化类型：门锁=3
const CURRENT_DEVICE_TYPE = { value: 3, label: '门锁' };

const LockList = () => {
  const [devices, setDevices] = useState([]);
  const [allDevices, setAllDevices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingDevice, setEditingDevice] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [displayColumns, setDisplayColumns] = useState([]);
   const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const loadRooms = async () => {
    try {
      const response = await getAllRooms();
      const roomList = response?.success && Array.isArray(response.data) ? response.data : [];
      setRooms(roomList);
    } catch (error) {
      console.error('加载房间失败：', error);
      message.error('加载房间列表失败');
    }
  };

  // ✅ 自动过滤【门锁】类型
  const loadDevices = async () => {
    try {
      setLoading(true);
      const response = await getAllDevices(CURRENT_DEVICE_TYPE.value);
      const deviceList = response?.success && Array.isArray(response.data) 
        ? response.data.filter(item => item.device_type === CURRENT_DEVICE_TYPE.value) 
        : [];
      setDevices(deviceList);
      setAllDevices(deviceList);
      message.success(`加载成功 ${deviceList.length} 个${CURRENT_DEVICE_TYPE.label}`);
    } catch (error) {
      console.error('加载设备失败：', error);
      const errorMsg = error.response?.data?.error || `加载${CURRENT_DEVICE_TYPE.label}列表失败，请稍后重试`;
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRooms();
    loadDevices();
  }, []);

  useEffect(() => {
    let filteredDevices = [...allDevices];
    if (searchText.trim()) {
      const keyword = searchText.trim();
      filteredDevices = filteredDevices.filter(device => 
        device.device_mac.includes(keyword) || 
        device.factory_meter_no.includes(keyword) || 
        device.current_meter_no.includes(keyword) ||
        (device.imei && String(device.imei).includes(keyword)) ||
        (device.room_name && device.room_name.includes(keyword)) ||
        (device.house_name && device.house_name.includes(keyword)) ||
        (device.lock_source && String(device.lock_source).includes(keyword))
      );
    }
    setDevices(filteredDevices);
  }, [searchText, allDevices]);

  useEffect(() => {
    setPagination((prev) => {
      const total = devices.length;
      const maxPage = Math.max(1, Math.ceil(total / prev.pageSize));
      return {
        ...prev,
        total,
        current: Math.min(prev.current, maxPage),
      };
    });
  }, [devices]);

  // 处理搜索
  const handleSearch = () => {};
  const handleReset = () => {
    setSearchText('');
    setPagination((prev) => ({ ...prev, current: 1 }));
  };
  const handleRefresh = () => loadDevices();
  const handleExport = () => {
    const exportData = devices.map(device => ({
      '门锁ID': device.id,
      '设备MAC': device.device_mac || '-',
      '厂商编号': device.factory_meter_no || '-',
      '当前编号': device.current_meter_no || '-',
      '是否审核': lockReviewLabel(device),
      '来源': String(device.lock_source || '').trim() || '-',
      'IMEI': device.imei || '-',
      '所属厂家': (() => {
        const code = String(device.manufacturer || '').trim();
        if (code === 'ZT') return '厂家一';
        return code || '-';
      })(),
      '设备状态': device.device_status_name || '-',
      '最低充值(元)': Number(device.min_recharge || 0).toFixed(2),
      '管理费(元)': Number(device.management_fee || 0).toFixed(2),
      '绑定房间': device.room_name || '-',
      '所属房源': device.house_name || '-',
      '创建时间': device.created_at || '-',
      '更新时间': device.updated_at || '-',
    }));
    if (exportToCSV(exportData, '门锁列表')) {
      message.success(`成功导出 ${exportData.length} 条数据`);
    } else {
      message.error('导出数据失败，请重试');
    }
  };

  // 下载批量导入模板
  const handleDownloadTemplate = async () => {
    try {
      await downloadDeviceTemplate();
      message.success('模板下载成功');
    } catch (error) {
      console.error('下载模板失败：', error);
      message.error('下载模板失败，请重试');
    }
  };

  // 批量导入设备
  const fileInputRef = useRef(null);
  const handleBatchImport = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      message.error('请选择CSV文件');
      return;
    }

    try {
      setLoading(true);
      const res = await batchImportDevices(file);
      showBatchImportResult(res, { entityName: '设备' });
      if (res?.success) {
        loadDevices();
      }
    } catch (error) {
      console.error('批量导入失败：', error);
      showBatchImportHttpError(error);
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

    // 分页变化处理
  const handleTableChange = (newPagination) => {
    setPagination({
          current: newPagination.current,
          pageSize: newPagination.pageSize,
          total: devices.length,
        });
    console.log('分页变化：', newPagination);
  };

  const pagedDevices = devices.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  const showModal = (device = null) => {
    setEditingDevice(device);
    if (device) {
      form.setFieldsValue({
        device_type: device.device_type != null ? Number(device.device_type) : CURRENT_DEVICE_TYPE.value,
        device_mac: device.device_mac,
        factory_meter_no: device.factory_meter_no,
        current_meter_no: device.current_meter_no,
        device_status: device.device_status.toString(),
        min_recharge: device.min_recharge || 0,
        management_fee: device.management_fee || 0,
        bind_room_id: device.bind_room_id ? device.bind_room_id.toString() : '',
        lock_source: (() => {
          const s = String(device.lock_source || '').trim();
          return s === '公司' || s === '其他' ? s : undefined;
        })(),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        device_type: CURRENT_DEVICE_TYPE.value,
      });
    }
    setIsModalVisible(true);
  };

  // ✅ 自动携带【门锁类型=3】
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      setLoading(true);
      if (editingDevice) {
        const submitData = {
          ...values,
          device_type: values.device_type ? parseInt(values.device_type, 10) : CURRENT_DEVICE_TYPE.value,
          device_status: parseInt(values.device_status),
          bind_room_id: values.bind_room_id ? parseInt(values.bind_room_id) : 0,
          min_recharge: parseFloat(values.min_recharge) || 0,
          management_fee: parseFloat(values.management_fee) || 0,
          electric_balance: 0,
          electric_quantity: 0,
        };
        delete submitData.device_type;
        if (submitData.device_mac !== undefined && submitData.device_mac !== null) {
          submitData.device_mac = String(submitData.device_mac).trim() || null;
        }
        submitData.lock_source =
          values.lock_source === undefined || values.lock_source === null ? '' : String(values.lock_source);
        const res = await updateDevice(editingDevice.id, submitData);
        res?.success ? message.success(`编辑${CURRENT_DEVICE_TYPE.label}成功`) 
                    : message.error(`编辑${CURRENT_DEVICE_TYPE.label}失败：` + (res?.error || '后端异常'));
      } else {
        const targetDeviceType = values.device_type ? parseInt(values.device_type, 10) : CURRENT_DEVICE_TYPE.value;
        const submitData = {
          current_meter_no: values.current_meter_no,
          device_type: targetDeviceType,
          device_status: 1, // 新增默认在线
          device_mac: values.device_mac ? String(values.device_mac).trim() || undefined : undefined,
          bind_room_id: 0,
          min_recharge: 0,
          management_fee: 0,
          electric_balance: 0,
          electric_quantity: 0,
          lock_source:
            values.lock_source === undefined || values.lock_source === null ? '' : String(values.lock_source),
        };
        const res = await addDevice(submitData);
        const deviceTypeName = DEVICE_TYPE_OPTIONS.find(opt => opt.value === targetDeviceType)?.label || '设备';
        res?.success ? message.success(`新增${deviceTypeName}成功`) 
                    : message.error(`新增${deviceTypeName}失败：` + (res?.error || '后端异常'));
      }
      setIsModalVisible(false);
      loadDevices();
    } catch (error) {
      console.error('提交表单失败：', error);
      const errMsg = error?.response?.data?.error || error?.response?.data?.msg || error?.message || '提交失败，请检查输入内容';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`确定要删除该${CURRENT_DEVICE_TYPE.label}吗？删除后不可恢复！`)) return;
    try {
      setLoading(true);
      await deleteDevice(id);
      message.success(`删除${CURRENT_DEVICE_TYPE.label}成功`);
      loadDevices();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('删除失败：', error);
      message.error(`删除${CURRENT_DEVICE_TYPE.label}失败，请稍后重试`);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(`请至少选择一个${CURRENT_DEVICE_TYPE.label}进行删除`);
      return;
    }
    if (!window.confirm(`确定要删除选中的${selectedRowKeys.length}个${CURRENT_DEVICE_TYPE.label}吗？删除后不可恢复！`)) return;
    try {
      setLoading(true);
      await batchDeleteDevices(selectedRowKeys);
      message.success(`成功删除${selectedRowKeys.length}个${CURRENT_DEVICE_TYPE.label}`);
      loadDevices();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('批量删除失败：', error);
      message.error(`批量删除${CURRENT_DEVICE_TYPE.label}失败，请稍后重试`);
    } finally {
      setLoading(false);
    }
  };

  // 原始表格列配置
  const originalColumns = [
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => showModal(record)}>编辑</Button>
      ),
    },
    { title: '门锁ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: '设备MAC', dataIndex: 'device_mac', key: 'device_mac', width: 140 },
    { title: '厂商编号', dataIndex: 'factory_meter_no', key: 'factory_meter_no', width: 130 },
    { title: '当前编号', dataIndex: 'current_meter_no', key: 'current_meter_no', width: 130 },
    {
      title: '是否审核',
      key: 'lock_reviewed',
      dataIndex: 'lock_reviewed',
      width: 88,
      render: (_, record) => lockReviewLabel(record),
    },
    {
      title: '来源',
      key: 'lock_source',
      dataIndex: 'lock_source',
      width: 88,
      render: (v) => (String(v || '').trim() ? String(v).trim() : '—'),
    },
    {
      title: '通讯类型',
      dataIndex: 'communication_type',
      key: 'communication_type',
      width: 100,
      render: (_, record) => {
        const t = Number(record?.communication_type);
        if (t === 0) return '4G';
        if (t === 1) return '蓝牙';
        if (t === 2) return 'WIFI';
        return '-';
      },
    },
    { title: '在线状态', dataIndex: 'is_online_name', key: 'is_online_name', width: 100 },
    { title: '设备详细种类', dataIndex: 'device_detail_type', key: 'device_detail_type', width: 130, render: (v) => (v == null || v === '' ? '-' : String(v)) },
    { title: '门锁电量', dataIndex: 'lock_battery', key: 'lock_battery', width: 100, render: (v) => (v == null ? '-' : `${v}%`) },
    {
      title: '设备厂家',
      dataIndex: 'manufacturer',
      key: 'manufacturer',
      width: 140,
      render: (v) => {
        const code = String(v || '').trim();
        if (code === 'ZT') return '厂家一';
        return code || '-';
      },
    },
    { title: 'IMEI', dataIndex: 'imei', key: 'imei', width: 160, render: (v) => (v ? String(v) : '-') },
    { title: '设备状态', dataIndex: 'device_status_name', key: 'device_status_name', width: 90 },
    { title: '最低充值(元)', dataIndex: 'min_recharge', key: 'min_recharge', width: 100, render: (v) => (v || 0).toFixed(2) },
    { title: '管理费(元)', dataIndex: 'management_fee', key: 'management_fee', width: 100, render: (v) => (v || 0).toFixed(2) },
    { title: '绑定房间', dataIndex: 'room_name', key: 'room_name', width: 100 },
    { title: '所属房源', dataIndex: 'house_name', key: 'house_name', width: 150 },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160 },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 160 },
    {
      title: '删除',
      key: 'delete_action',
      width: 88,
      render: (_, record) => (
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
          删除
        </Button>
      ),
    },
  ];

  // 从localStorage加载列设置并应用到表格（只处理隐藏）
  useEffect(() => {
    const storageKey = 'lockList_columnStates';
    const saved = localStorage.getItem(storageKey);
    let columnStates = {};
    
    if (saved) {
      try {
        columnStates = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse column states:', e);
      }
    }
    
    // 根据列状态生成实际列配置（只处理隐藏）
    const processedColumns = originalColumns.map(col => {
      const state = columnStates[col.key];
      
      if (state === 'hidden') {
        return null;
      }
      
      return col;
    }).filter(col => col !== null);
    
    setDisplayColumns(processedColumns);
  }, []);

  const rowSelection = { selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) };

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title={`${CURRENT_DEVICE_TYPE.label}管理`} />
      <ListToolbar
        searchPlaceholder="搜索MAC、编号、房间或房源名称"
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        onReset={handleReset}
        onRefresh={handleRefresh}
        onExport={handleExport}
        showBatchDelete={true}
        selectedCount={selectedRowKeys.length}
        onBatchDelete={handleBatchDelete}
        showAdd={true}
        onAdd={() => showModal()}
        actions={[
          {
            key: 'download-template',
            label: '下载模板',
            icon: <DownloadOutlined />,
            onClick: handleDownloadTemplate,
            type: 'default',
          },
          {
            key: 'batch-import',
            label: '批量导入',
            icon: <UploadOutlined />,
            onClick: handleBatchImport,
            type: 'default',
          },
        ]}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <ResizableTable
        columns={displayColumns.length > 0 ? displayColumns : originalColumns}
        dataSource={pagedDevices}
        loading={loading}
        rowKey="id"
        storageKey="lockList"
        rowSelection={rowSelection}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条数据`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        scroll={{ x: 1800 }}
        onChange={handleTableChange}
        locale={{ emptyText: <Empty description={`暂无${CURRENT_DEVICE_TYPE.label}数据`} /> }}
      />
      <Modal
        title={editingDevice ? `编辑${CURRENT_DEVICE_TYPE.label}` : '新增设备'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSubmit}
        width={600}
        destroyOnClose
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          {editingDevice ? (
            <>
              <Form.Item
                name="device_type"
                label="设备类型"
                rules={[{ required: true, message: '请选择设备类型' }]}
              >
                <Select placeholder="请选择设备类型" options={DEVICE_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                name="device_mac"
                label="设备MAC"
                rules={[{ required: true, message: '请输入设备MAC地址' }, { max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="选填" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="factory_meter_no"
                label="厂商编号"
                rules={[{ max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="请输入厂商编号" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="current_meter_no"
                label="当前编号"
                rules={[{ required: true, message: '请输入当前编号' }, { max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="请输入当前编号" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="lock_source"
                label="门锁来源"
                tooltip="清空为未设置（未审核）；选「公司」或「其他」保存后自动记为已审核"
              >
                <Select placeholder="未设置（未审核）" allowClear options={LOCK_SOURCE_EDIT_OPTIONS} />
              </Form.Item>
              <Form.Item
                name="device_status"
                label="设备状态"
                rules={[{ required: true, message: '请选择设备状态' }]}
              >
                <Select placeholder="请选择设备状态" options={DEVICE_STATUS_OPTIONS} />
              </Form.Item>
              <Form.Item name="bind_room_id" label="绑定房间">
                <Select placeholder="请选择绑定房间（可选）" allowClear>
                  {rooms.map(room => (
                    <Option key={room.id} value={room.id.toString()}>
                      {(room.houseName ?? room.house_name ?? '-') + ' - ' + (room.number ?? room.fang_hao ?? '-')}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Form.Item
                  name="min_recharge"
                  label="最低充值（元）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效金额（最多两位小数）' }]}
                >
                  <Input placeholder="默认0.00" />
                </Form.Item>
                <Form.Item
                  name="management_fee"
                  label="管理费（元）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效金额（最多两位小数）' }]}
                >
                  <Input placeholder="默认0.00" />
                </Form.Item>
              </Space.Compact>
            </>
          ) : (
            <>
              <Form.Item
                name="device_type"
                label="设备类型"
                rules={[{ required: true, message: '请选择设备类型' }]}
              >
                <Select placeholder="请选择设备类型" options={DEVICE_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                name="current_meter_no"
                label="当前编号"
                rules={[{ required: true, message: '请输入当前编号' }, { max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="请输入当前编号（必填）" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="device_mac"
                label="设备MAC"
                rules={[{ max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="选填" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="lock_source"
                label="门锁来源"
                tooltip="清空为未设置（未审核）；选「公司」或「其他」保存后自动记为已审核"
              >
                <Select placeholder="未设置（未审核）" allowClear options={LOCK_SOURCE_EDIT_OPTIONS} />
              </Form.Item>
              <div style={{ color: '#999', fontSize: '12px', marginTop: '-16px', marginBottom: '16px' }}>
                其他字段将使用默认值：设备状态=在线/正常，余额=0，未绑定房间
              </div>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};
export default LockList;